import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RedeemIcon from '@mui/icons-material/Redeem';
import { AISystemsService } from 'services/AISystemService';
import { Toast, Utils } from 'shared/helpers';
import { useParams } from 'react-router-dom';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { ReactComponent as Document } from 'assets/images/ic_document.svg';

export interface FeddbackProps {
  _id: string;
  aisystemId: string;
  name: string;
  email: string;
  feedbackType: string;
  category: string;
  feedback: string;
  organizationId: string;
  isActive: boolean;
  isReviewd: boolean;
  reviewdBy: any;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const Feedback = (): ReactElement => {
  const [feedbackList, setFeedback] = useState<FeddbackProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { aiSystemId } = useParams();

  const getFeedback = (): void => {
    if (!aiSystemId) return;
    setIsLoading(true);
    AISystemsService.getFeedbackList(aiSystemId)
      .then((res) => {
        const { data } = res.data;
        setFeedback(data);
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const actionOnUpdate = (id: string, isReviewed: boolean): void => {
    AISystemsService.updateFeedback(id, isReviewed)
      .then(() => {
        Toast.showSuccessMessage('Feedback updated successfully');
        getFeedback();
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      });
  };

  useEffect(() => {
    getFeedback();
  }, []);

  return (
    <Box>
      {!isLoading && feedbackList.length > 0 && (
        <Grid container spacing={3}>
          <Grid item md={3}>
            <Card className="p-4">
              <Box className="flex">
                <ChatBubbleOutlineIcon
                  className="mt-4"
                  style={{ color: '#0a70cf' }}
                />
                <Box className="ml-4">
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Total
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'text.primary' }}>
                    {feedbackList.length}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item md={3}>
            <Card className="p-4">
              <Box className="flex">
                <AccessTimeIcon className="mt-4" style={{ color: '#eda137' }} />
                <Box className="ml-4">
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Pending
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'text.primary' }}>
                    {feedbackList.filter((x) => !x.isReviewd).length}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item md={3}>
            <Card className="p-4">
              <Box className="flex">
                <CheckCircleOutlineIcon
                  className="mt-4"
                  style={{ color: '#00b00c' }}
                />
                <Box className="ml-4">
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Reviewed
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'text.primary' }}>
                    {feedbackList.filter((x) => x.isReviewd).length}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item md={3}>
            <Card className="p-4">
              <Box className="flex">
                <RedeemIcon className="mt-4" style={{ color: '#9d009d' }} />
                <Box className="ml-4">
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Recent
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'text.primary' }}>
                    {feedbackList.length}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}
      <Box className="feedback-container mt-5">
        {!isLoading && feedbackList.length > 0 && (
          <>
            {feedbackList.map((feedback) => (
              <Card
                className="mt-4 p-4"
                sx={{ borderLeft: '5px solid #eda137' }}
              >
                <Box className="justify-space">
                  <Box className="flex mt- mb-2">
                    <Chip
                      label={feedback.feedbackType}
                      className="mx-2"
                      sx={{ background: '#f8bff8', color: '#9d009d' }}
                      size="small"
                    />
                    <Chip
                      label={feedback.category}
                      className="mx-2"
                      sx={{ background: '#e5e8e6', color: 'black' }}
                      size="small"
                    />
                    {feedback.isReviewd ? (
                      <Chip
                        label="Reviewed"
                        className="mx-2"
                        sx={{ background: '#93fb9a', color: '#00b00c' }}
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Pending Review"
                        className="mx-2"
                        sx={{ background: '#f7e3c7', color: '#eda137' }}
                        size="small"
                      />
                    )}
                  </Box>
                  {!feedback.isReviewd && (
                    <Box
                      className="mx-2 p-2 flex cursor-pointer"
                      sx={{ border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      <CheckCircleOutlineIcon
                        className="mx-2"
                        fontSize="small"
                      />

                      <Typography
                        variant="subtitle2"
                        onClick={() => actionOnUpdate(feedback._id, true)}
                      >
                        Mark as Reviewed
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box className="flex ">
                  <PersonOutlinedIcon fontSize="small" className="mr-1 ml-2 " />
                  <Typography variant="body1">{feedback.name} </Typography>
                  <EmailOutlinedIcon className="mr-1 ml-5 " fontSize="small" />
                  <Typography variant="body1">{feedback.email} </Typography>
                </Box>
                <Typography
                  className="mx-3 mt-2"
                  variant="body1"
                  sx={{ color: 'text.primary' }}
                >
                  {feedback.feedback}
                </Typography>
              </Card>
            ))}
          </>
        )}
        {!isLoading && feedbackList.length === 0 && (
          <Card variant="outlined">
            <CardContent>
              <SvgIcon
                component={Document}
                inheritViewBox
                className="document-icon"
                fontSize="inherit"
              />
              <Typography className="policy-error-title mt-2" component="span">
                No Feedback yet
              </Typography>
              <Typography className="policy-description">
                No feedback has been submitted for this AI solution yet.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default Feedback;
